/**
 * PrepressContext — the artwork → proof → blueprint → release lifecycle.
 * Covers intake, validation, proofing, revisions, approvals, blueprint/SKU,
 * dieline/color/tooling, release/hold, archive, and technical notes.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

export type PrepStatus = 'intake' | 'validating' | 'proofing' | 'client_review' | 'revision' | 'approved' | 'released' | 'on_hold';
export type Check = 'pass' | 'fail' | 'pending';
export const CHECK_ITEMS = ['Dimensions', 'Color mode', 'Resolution', 'Fonts', 'Dieline', 'Bleed', 'Layers', 'File type'] as const;
export type CheckItem = typeof CHECK_ITEMS[number];

export interface PrepFile { id: string; name: string; kind: string; }
export interface Proof { id: string; version: number; status: 'internal' | 'sent' | 'approved' | 'revision'; }
export interface Evt { id: string; text: string; time: string; }
export interface PrepJob {
  id: string; created: string; clientName: string; sku?: string; product: string; source: string; status: PrepStatus;
  files: PrepFile[];
  validation: Record<CheckItem, Check>;
  proofs: Proof[];
  revisions: Evt[];
  missingInfo: string[];
  diffReport?: string;
  blueprint?: { id: string; created: string };
  skuRecord?: { sku: string; specs: string };
  dieline?: string;
  color?: { pantone: string; cmyk: string; lab: string; density: string };
  tooling?: { plate: string; die: string; cylinder: string };
  approvals: Evt[];
  hold?: string;
  archive?: string;
  notes: Evt[];
  history: Evt[];
}

const emptyChecks = (): Record<CheckItem, Check> => Object.fromEntries(CHECK_ITEMS.map((c) => [c, 'pending'])) as Record<CheckItem, Check>;
let seq = 3;
const newId = () => `PP-2026-${String(seq++).padStart(4, '0')}`;
const now = () => 'Just now';
const evt = (text: string): Evt => ({ id: `e${Date.now()}${Math.random()}`, text, time: now() });

const SEED: PrepJob[] = [
  {
    id: 'PP-2026-0001', created: 'Today 9:30 AM', clientName: 'Acme Nutraceutical Packaging', sku: 'SKU-ACME-0007', product: 'Stand-up pouch', source: 'Portal upload', status: 'validating',
    files: [{ id: 'f1', name: 'ACME_0007_art_v3.ai', kind: 'artwork' }, { id: 'f2', name: 'ACME_0007_dieline.pdf', kind: 'dieline' }],
    validation: { ...emptyChecks(), Dimensions: 'pass', 'Color mode': 'pass', Resolution: 'fail' },
    proofs: [], revisions: [], missingInfo: [], approvals: [], notes: [], history: [evt('Artwork intake received from portal.')],
  },
  {
    id: 'PP-2026-0002', created: 'Yesterday 2:10 PM', clientName: 'Blue Harbor Foods', sku: 'SKU-BLUE-0003', product: 'Shrink sleeve', source: 'Email from CSR', status: 'client_review',
    files: [{ id: 'f3', name: 'BlueHarbor_sleeve_v2.pdf', kind: 'artwork' }],
    validation: { ...emptyChecks(), Dimensions: 'pass', 'Color mode': 'pass', Resolution: 'pass', Fonts: 'pass', Dieline: 'pass', Bleed: 'pass', Layers: 'pass', 'File type': 'pass' },
    proofs: [{ id: 'pr1', version: 2, status: 'sent' }], revisions: [], missingInfo: [], approvals: [], notes: [], history: [evt('Proof v2 sent to client for review.')],
  },
];

interface Ctx {
  jobs: PrepJob[];
  createJob: (j: Partial<PrepJob>) => string;
  attachFile: (id: string, name: string, kind: string) => void;
  validate: (id: string, item: CheckItem, val: Check) => void;
  requestInfo: (id: string, items: string[]) => void;
  createProof: (id: string) => void;
  sendProof: (id: string, proofId: string) => void;
  logRevision: (id: string, note: string) => void;
  compareVersions: (id: string) => void;
  approveInternal: (id: string, proofId: string) => void;
  captureApproval: (id: string, via: string) => void;
  createBlueprint: (id: string) => void;
  setSku: (id: string, sku: string, specs: string) => void;
  linkDieline: (id: string, name: string) => void;
  setColor: (id: string, c: PrepJob['color']) => void;
  setTooling: (id: string, t: PrepJob['tooling']) => void;
  release: (id: string) => void;
  placeHold: (id: string, reason: string) => void;
  archive: (id: string, link: string) => void;
  addNote: (id: string, text: string) => void;
  updateStatus: (id: string, status: PrepStatus) => void;
}

const C = createContext<Ctx | null>(null);

export function PrepressProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<PrepJob[]>(SEED);
  const patch = (id: string, fn: (j: PrepJob) => PrepJob) => setJobs((js) => js.map((j) => (j.id === id ? fn(j) : j)));
  const log = (j: PrepJob, text: string): PrepJob => ({ ...j, history: [evt(text), ...j.history] });

  const createJob = useCallback((j: Partial<PrepJob>) => {
    const id = newId();
    const job: PrepJob = {
      id, created: now(), clientName: j.clientName || 'Unknown client', sku: j.sku, product: j.product || '—', source: j.source || 'Manual', status: 'intake',
      files: j.files || [], validation: emptyChecks(), proofs: [], revisions: [], missingInfo: [], approvals: [], notes: [], history: [evt('Artwork intake created.')],
    };
    setJobs((js) => [job, ...js]);
    return id;
  }, []);

  const attachFile = useCallback((id: string, name: string, kind: string) => patch(id, (j) => log({ ...j, files: [...j.files, { id: `f${Date.now()}`, name, kind }] }, `File added: ${name}.`)), []);
  const validate = useCallback((id: string, item: CheckItem, val: Check) => patch(id, (j) => log({ ...j, status: j.status === 'intake' ? 'validating' : j.status, validation: { ...j.validation, [item]: val } }, `${item} marked ${val}.`)), []);
  const requestInfo = useCallback((id: string, items: string[]) => patch(id, (j) => log({ ...j, missingInfo: [...new Set([...j.missingInfo, ...items])] }, `Requested missing info: ${items.join(', ')}.`)), []);
  const createProof = useCallback((id: string) => patch(id, (j) => log({ ...j, status: 'proofing', proofs: [...j.proofs, { id: `pr${Date.now()}`, version: j.proofs.length + 1, status: 'internal' }] }, `Proof v${j.proofs.length + 1} created.`)), []);
  const sendProof = useCallback((id: string, proofId: string) => patch(id, (j) => log({ ...j, status: 'client_review', proofs: j.proofs.map((p) => (p.id === proofId ? { ...p, status: 'sent' } : p)) }, `Proof sent for review.`)), []);
  const logRevision = useCallback((id: string, note: string) => patch(id, (j) => log({ ...j, status: 'revision', revisions: [...j.revisions, evt(note)] }, `Revision logged: ${note}`)), []);
  const compareVersions = useCallback((id: string) => patch(id, (j) => {
    const prev = j.proofs.length >= 2 ? `v${j.proofs.length - 1} → v${j.proofs.length}` : 'baseline → current';
    return log({ ...j, diffReport: `Difference report (${prev}): color target shifted on 1 element, dieline unchanged, 2 text edits applied, bleed within spec.` }, 'Version comparison generated.');
  }), []);
  const approveInternal = useCallback((id: string, proofId: string) => patch(id, (j) => log({ ...j, proofs: j.proofs.map((p) => (p.id === proofId ? { ...p, status: 'approved' } : p)) }, 'Internal proof approved — ready for client/production.')), []);
  const captureApproval = useCallback((id: string, via: string) => patch(id, (j) => log({ ...j, status: 'approved', approvals: [...j.approvals, evt(`Client approval captured via ${via}.`)] }, `Client approval captured (${via}).`)), []);
  const createBlueprint = useCallback((id: string) => patch(id, (j) => log({ ...j, blueprint: { id: `BP-${j.sku || 'SKU'}-V0${j.proofs.length || 1}`, created: now() } }, 'Blueprint created (production-ready spec).')), []);
  const setSku = useCallback((id: string, sku: string, specs: string) => patch(id, (j) => log({ ...j, sku, skuRecord: { sku, specs } }, `SKU record updated: ${sku}.`)), []);
  const linkDieline = useCallback((id: string, name: string) => patch(id, (j) => log({ ...j, dieline: name }, `Dieline linked: ${name}.`)), []);
  const setColor = useCallback((id: string, c: PrepJob['color']) => patch(id, (j) => log({ ...j, color: c }, 'Color standard recorded.')), []);
  const setTooling = useCallback((id: string, t: PrepJob['tooling']) => patch(id, (j) => log({ ...j, tooling: t }, 'Plate/tooling info recorded.')), []);
  const release = useCallback((id: string) => patch(id, (j) => log({ ...j, status: 'released', hold: undefined }, 'Released to production — prepress green light.')), []);
  const placeHold = useCallback((id: string, reason: string) => patch(id, (j) => log({ ...j, status: 'on_hold', hold: reason }, `Prepress hold placed: ${reason}`)), []);
  const archive = useCallback((id: string, link: string) => patch(id, (j) => log({ ...j, archive: link }, `Approved files archived: ${link}.`)), []);
  const addNote = useCallback((id: string, text: string) => patch(id, (j) => ({ ...j, notes: [evt(text), ...j.notes] })), []);
  const updateStatus = useCallback((id: string, status: PrepStatus) => patch(id, (j) => log({ ...j, status }, `Status: ${status.replace('_', ' ')}.`)), []);

  return <C.Provider value={{ jobs, createJob, attachFile, validate, requestInfo, createProof, sendProof, logRevision, compareVersions, approveInternal, captureApproval, createBlueprint, setSku, linkDieline, setColor, setTooling, release, placeHold, archive, addNote, updateStatus }}>{children}</C.Provider>;
}

export function usePrepress() {
  const c = useContext(C);
  if (!c) throw new Error('usePrepress must be used within PrepressProvider');
  return c;
}
